import { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";
import { COUNTRY_CODES, defaultCountryFromLocale } from "@/lib/constants";

/**
 * Phone input with a searchable country code picker. The default country is chosen
 * from the browser's locale (India by default). Value is stored as the full E.164-ish
 * string "<dial> <local>" — the parent form can use it as-is or split as needed.
 */
export default function PhoneInput({ value, onChange, required, testid = "phone-input" }) {
  const [country, setCountry] = useState(defaultCountryFromLocale());
  const [local, setLocal] = useState("");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  // Keep parent in sync
  useEffect(() => { onChange(`${country.dial} ${local}`.trim()); /* eslint-disable-next-line */ }, [country, local]);

  // Hydrate from external value once
  useEffect(() => {
    if (!value) return;
    const match = COUNTRY_CODES.find((c) => value.startsWith(c.dial));
    if (match) {
      setCountry(match);
      setLocal(value.slice(match.dial.length).trim());
    } else {
      setLocal(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const click = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", click);
    return () => document.removeEventListener("mousedown", click);
  }, []);

  const filtered = query
    ? COUNTRY_CODES.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()) || c.dial.includes(query))
    : COUNTRY_CODES;

  return (
    <div ref={ref} className="relative flex items-center overflow-visible rounded-lg border border-forest/20 bg-parchment/60 focus-within:border-forest">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-country`}
        className="flex items-center gap-1.5 border-r border-forest/15 px-3 py-3 text-sm hover:bg-mist/40"
      >
        <span className="text-lg leading-none">{country.flag}</span>
        <span className="font-medium text-forest">{country.dial}</span>
        <ChevronDown size={14} className="text-noir/60" />
      </button>
      <input
        type="tel"
        required={required}
        value={local}
        onChange={(e) => setLocal(e.target.value.replace(/[^\d\s-]/g, ""))}
        placeholder="Phone number"
        className="flex-1 bg-transparent px-4 py-3 focus:outline-none"
        data-testid={testid}
      />

      {open && (
        <div className="absolute left-0 top-full z-40 mt-1 w-72 rounded-xl border border-forest/15 bg-white p-2 shadow-card">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search country"
            className="mb-2 w-full rounded-md border border-forest/15 px-3 py-2 text-sm focus:outline-none"
            data-testid={`${testid}-search`}
          />
          <div className="max-h-72 overflow-y-auto">
            {filtered.map((c) => (
              <button
                key={`${c.code}-${c.dial}`}
                type="button"
                onClick={() => { setCountry(c); setOpen(false); setQuery(""); }}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-mist"
                data-testid={`${testid}-opt-${c.code}`}
              >
                <span className="text-lg leading-none">{c.flag}</span>
                <span className="flex-1">{c.name}</span>
                <span className="text-noir/60">{c.dial}</span>
              </button>
            ))}
            {filtered.length === 0 && <p className="px-3 py-3 text-sm text-noir/60">No matches</p>}
          </div>
        </div>
      )}
    </div>
  );
}
